import { Controller } from "@hotwired/stimulus";
import * as echarts from "echarts";
import moment from "moment-timezone";
import { localTZ } from "../lib/date_helpers";

export default class extends Controller {
  static targets = ["chartBody"];

  static values = {
    timezone: String,
    data: Array,
  };

  connect() {
    this.chart = null;
    this.initializeChart();
    this.updateData();
  }

  initializeChart() {
    if (!this.hasChartBodyTarget) {
      return;
    }

    this.chart = echarts.init(this.chartBodyTarget);
    this.chart.setOption({
      tooltip: {
        trigger: "axis",
        valueFormatter: (value) => this.formatTime(value),
        axisPointer: {
          label: {
            formatter: (params) =>
              moment(params.value).format("dddd, MMM D, h:mm a"),
          },
        },
      },
      xAxis: {
        type: "time",
      },
      yAxis: {
        type: "value",
        axisLabel: { formatter: (value) => this.formatTime(value) },
      },
      series: [
        {
          name: "Response Time",
          type: "line",
          color: ["#2caffe"],
        },
      ],
      grid: {
        containLabel: true,
        left: "10",
        right: "15",
        top: "20",
        bottom: "10",
      },
    });
  }

  updateData() {
    this.responseTimeData = this.dataValue.map((data) => {
      // Echarts renders all times in the browsers local timezone. At the time
      // of this commit there is no way to specify what timezone echarts should
      // use. To get around this, we manipulate all the timestamps by adding
      // the offset indicated by the selected timezone.
      return [this.utcToZonedTime(data[0], this.timezoneValue), data[1]];
    });
    this.redrawChart();
    this.resizeChart();
  }

  redrawChart() {
    if (!this.chart) {
      return;
    }

    this.chart.setOption({
      series: [
        {
          name: "Response Time",
          data: this.responseTimeData,
        },
      ],
    });
  }

  resizeChart() {
    if (!this.chart) {
      return;
    }

    this.chart.resize();
  }

  formatTime(v) {
    if (v > 1000) {
      return `${Math.round((v / 1000) * 10) / 10} s`;
    }

    return `${Math.round(v)} ms`;
  }

  // This function manipulates a given timestamp by the offset of the given
  // timezone. The result of this function should be used for display purposes
  // only!
  utcToZonedTime(utcTime, timezone) {
    const ourTimezone = localTZ();
    const ourOffsetInMillis =
      moment.utc(utcTime).tz(ourTimezone).utcOffset() * 60000;
    const givenTimezoneOffsetInMillis =
      moment.utc(utcTime).tz(timezone).utcOffset() * 60000;

    return utcTime + givenTimezoneOffsetInMillis - ourOffsetInMillis;
  }
}
